/**
 * Basic popover model/view implementation
 * Simple Usage:
 * var popover = new Popover.View({
 *    base: DomElement
 * });
 *
 * see usages for more advanced things
 */
import { captureException } from "extensions/sentry";

import { View } from "extensions/marionette";
import _ from "extensions/underscore";
import Backbone from "extensions/backbone";
import $ from "jquery";

import popoverTemplate from "./popover.jst";

export const PopoverModel = Backbone.Model.extend({
    defaults: {
        title: "",
        placement: "bottom",
        delay: 0,
        container: false,
        className: "wide",
        
        // How popover is triggered
        // click | hover | focus | manual. You may pass multiple triggers; separate them with a space.
        trigger: "click",
        
        showCloseButton: true,
        dismissable: true
    }
});

export const PopoverView = View.extend({
    template() {}, // Noop here as template is handled by the bootstrap popover
    initialize(options) {
        if (options.render) {
            this.render();
        }
        if (options.content) {
            this.setContent(options.content);
        }
    },
    /**
     * Namespace a string with view's unique id
     * @param {string} eventName event name to be namespaced
     * @returns {string}
     */
    namespaceEvent(eventName) {
        return [eventName, this.cid].join(".");
    },
    serializePopoverData() {
        let data = {};
        
        if (this.model) {
            data = _.partial(this.serializeModel, this.model).apply(this, arguments);
        }
        
        return data;
    },
    
    createBootstrapPopover() {
        this.popover = this.popoverElement.popover({
            container: this.model.get("container"),
            title: this.model.get("title"),
            placement: this.model.get("placement"),
            trigger: this.model.get("trigger"),
            delay: this.model.get("delay") || 0,
            template: popoverTemplate(this.serializePopoverData())
        }).data("bs.popover");
    },
    
    /**
     * Bind events to hide the popover when certain events happen
     */
    bindHideEvents() {
        if (!this.popover.$tip) {
            return;
        }
        this.popover.$button = this.popover.$tip.find("[data-dismiss='popover']");
        
        // Bind event to listen for clicks outside of the popover boundaries
        this.popover.$viewport.on(this.namespaceEvent("click"), _.bind(function(e) {
            // If the event came from outside of the popover then hide it
            if (this.popover && this.popover.shown && (this.popover.$tip === null || this.popover.$tip[0] !== $(e.target).closest(".popover")[0])) {
                this._hide();
            }
        }, this));
        
        this.popover.$button.on(this.namespaceEvent("click"), _.bind(this._hide, this));
    },
    
    unbindHideEvents() {
        if (!this.popover.$tip) {
            return;
        }
        this.popover.$button.off(this.namespaceEvent("click"));
        this.popover.$viewport.off(this.namespaceEvent("click"));
    },
    
    onRender() {
        // eslint-disable-next-line eqeqeq
        if (this.model == null) {
            this.model = new PopoverModel();
        }
        
        const popoverElement = this.getOption("base");
        if (!popoverElement) {
            throw new Error("Provide base element to popover view");
        }
        this.popoverElement = $(popoverElement);
        this.createBootstrapPopover();
        this.popoverElement.on("shown.bs.popover", _.bind(function() {
            this.popover.shown = true;
        }, this));
        
        this._show();
        
        if (this.model.get("dismissable") === true) {
            this.bindHideEvents();
        }
    },
    
    _show() {
        this.triggerMethod("before:show:popover", this.popoverElement);
        this.popoverElement.popover("show");
        this.triggerMethod("on:show:popover", this.popoverElement);
        
        // Hide and disable tooltip on popoverElement when a popover is shown
        const tooltip = this.popoverElement.data("bs.tooltip");
        if (tooltip) {
            tooltip.hide();
            tooltip.disable();
        }
        
        if (!this.popover.$tip) {
            // Return earlier if there is no $tip available as it will throw an error
            return;
        }
        
        // Set default element to popover's container
        this.setElement(this.popover.$tip);
        
        // Add content region to follow layoutView behavior
        this.addRegions({
            content: ".popover-content"
        });
        
        if (this.getOption("className")) {
            this.$el.addClass(this.getOption("className"));
        }
        
        if (!this.model.get("dismissable")) {
            this.$el.addClass("no-dismiss");
        }
        
        // After applying the className need to call show again to adjust positioning
        this.popoverElement.popover("show");
    },
    
    _hide() {
        return this.onDestroy();
    },
    
    onDestroy() {
        if (this.isDestroyed()) { return; }
        
        this.popover.shown = false;
        if (this.model.get("dismissable") === true) {
            this.unbindHideEvents();
        }
        this.popoverElement.off();
        this.popoverElement.popover("destroy");
        this.popoverElement.removeData("bs.popover");
        this.popover = null;
        
        // Enable tooltip on popoverElement when popover is destroyed
        const tooltip = this.popoverElement.data("bs.tooltip");
        if (tooltip) {
            tooltip.enable();
        }
    },
    
    changeContentHeightTo(size) {
        this.$el.find(".popover-content").height(size);
    },
    /**
     * Proxy view insertion to view's Region Manager
     */
    setContent(view) {
        this.listenTo(view, "close:parent", () => {
            this.unbindHideEvents();
            window.setTimeout(() => {
                this.destroy();
            }, 400);
        });
        try {
            this.showChildView("content", view);
        } catch (err) {
            captureException(err);
        }
    }
});

export const show = (opts) => new PopoverView(opts);
